import { usePermissionsForCurrentUser } from '@retail/app/stores/selected-context';
import { useMemo } from 'react';
import { HierarchyFilterContext } from '@retail/my-assortment/types';
import {
  useFetchAssortmentHierarchyFilterOptions,
  useFetchSupplierHierarchyFilterOptions,
} from '@retail/my-assortment/data-access';
import { useFilters } from '@retail/products/hooks';
import { SelectedFilters } from '@retail/products/utils';
import { itemPricesFilters, ItemPricesFilters } from '@retail/my-assortment/utils';

interface Props {
  assortmentId: number;
  supplierNumber?: number;
}

export function useItemPricesCalculusViewFilters({ assortmentId, supplierNumber }: Props) {
  const { userCanRead } = usePermissionsForCurrentUser();
  const hasAccessToAssortment = useMemo(() => userCanRead('ASSORTMENT'), [userCanRead]);

  const isSupplierSelected = supplierNumber !== undefined;

  const { filters, setFilters, resetFilters } = useFilters<ItemPricesFilters>(itemPricesFilters);

  const supplierFilterOptions = useFetchSupplierHierarchyFilterOptions({
    assortmentId,
    supplierNumber: supplierNumber as number,
    inAssortment: hasAccessToAssortment ? !!filters.inAssortment : false,
    suspense: false,
    enabled: isSupplierSelected,
  });

  const assortmentFilterOptions = useFetchAssortmentHierarchyFilterOptions({
    assortmentId,
    suspense: false,
    enabled: !isSupplierSelected,
  });

  const {
    data: filterContextDto,
    error: errorFetchingFilters,
    isLoading: loadingFilters,
  } = isSupplierSelected ? supplierFilterOptions : assortmentFilterOptions;
  const filterContext = useMemo(
    () => (filterContextDto ? HierarchyFilterContext.fromDto(filterContextDto) : undefined),
    [filterContextDto]
  );

  return {
    errorFetchingFilters: !!errorFetchingFilters,
    loadingFilters,
    filterOptions: filterContext,
    selectedFilters: filters satisfies SelectedFilters<ItemPricesFilters>,
    setFilters,
    resetFilters,
  };
}
